import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import {
  ETYKIETA_ZAKRESU, tresciApi, type OdbiorcaWidoku, type PozycjaKatalogu, type SladSkorki, type UczenKatalogu, type WidokSkorki,
} from '@/lib/api/tresci';
import { oknoWarszawa } from '@/lib/tresci/biblioteka';
import { komunikatBledu } from '@/lib/tresci/tresciUi';
import { PodgladAplikacji } from './PodgladAplikacji';

/**
 * „SPRAWDŹ WIDOK UŻYTKOWNIKA" dla skórek (OWNER 16.10) — jak przy pasku: wynik liczy RESOLVER SYSTEMU GŁÓWNEGO,
 * ten sam, który odpowiada aplikacji (`GET api/app/seasonal-theme`). Panel nie ma własnych zasad pierwszeństwa.
 * Telefon rysuje DOKŁADNIE paczkę, którą dostałby wskazany odbiorca. Konto odbiorcy idzie w treści POST.
 */

type Kto = 'GOSC' | 'MIASTO' | 'SZKOLA' | 'UZYTKOWNIK';
const ETYKIETA_KTO: Readonly<Record<Kto, string>> = { GOSC: 'Gość', MIASTO: 'Miasto', SZKOLA: 'Szkoła', UZYTKOWNIK: 'Użytkownik' };

function Slad({ s }: { s: SladSkorki }) {
  return (
    <>
      <b>{s.name}</b> — {s.legacy ? 'publikacja globalna (V1)' : ETYKIETA_ZAKRESU[s.scope]} · priorytet {s.priority} · {oknoWarszawa(s.startsAt, s.endsAt)}
    </>
  );
}

export function WidokUzytkownikaSkorki({ onZamknij }: { onZamknij: () => void }) {
  const { api } = useServices();
  const [kto, setKto] = useState<Kto>('GOSC');
  const [miasta, setMiasta] = useState<PozycjaKatalogu[]>([]);
  const [miasto, setMiasto] = useState<PozycjaKatalogu | null>(null);
  const [szkolyMiasta, setSzkolyMiasta] = useState<PozycjaKatalogu[]>([]);
  const [szkola, setSzkola] = useState<PozycjaKatalogu | null>(null);
  const [fraza, setFraza] = useState('');
  const [wyniki, setWyniki] = useState<UczenKatalogu[]>([]);
  const [uczen, setUczen] = useState<UczenKatalogu | null>(null);
  const [szuka, setSzuka] = useState(false);
  const [wynik, setWynik] = useState<WidokSkorki | null>(null);
  // Paczka ŹRÓDŁOWA zwycięzcy (z biblioteki — zachowuje identyfikator katalogu do podglądu prawdziwej aplikacji).
  const [paczkaZrodlowa, setPaczkaZrodlowa] = useState<unknown>(undefined);
  const [blad, setBlad] = useState<string | null>(null);
  const [trwa, setTrwa] = useState(false);

  useEffect(() => {
    if (kto !== 'MIASTO' && kto !== 'SZKOLA') return;
    let aktualne = true;
    tresciApi.miasta(api).then((m) => { if (aktualne) setMiasta(m); }).catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, kto]);

  useEffect(() => {
    if (kto !== 'SZKOLA' || !miasto) return;
    let aktualne = true;
    tresciApi.szkoly(api, miasto.id).then((s) => { if (aktualne) setSzkolyMiasta(s); }).catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api, kto, miasto]);

  useEffect(() => {
    const q = fraza.trim();
    if (kto !== 'UZYTKOWNIK' || q.length < 3) return;
    let aktualne = true;
    const t = setTimeout(() => {
      setSzuka(true);
      tresciApi.uczniowie(api, q)
        .then((w) => { if (aktualne) setWyniki(w); })
        .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); })
        .finally(() => { if (aktualne) setSzuka(false); });
    }, 300);
    return () => { aktualne = false; clearTimeout(t); };
  }, [api, kto, fraza]);

  const odbiorca: OdbiorcaWidoku | null = kto === 'GOSC' ? {}
    : kto === 'MIASTO' ? (miasto ? { cityId: miasto.id } : null)
      : kto === 'SZKOLA' ? (szkola ? { schoolId: szkola.id } : null)
        : (uczen ? { userId: uczen.userId } : null);

  const sprawdz = async () => {
    if (!odbiorca) return;
    setTrwa(true);
    setBlad(null);
    try {
      const w = await tresciApi.widokSkorki(api, odbiorca);
      setWynik(w);
      setPaczkaZrodlowa(w?.winner ? (await tresciApi.skorka(api, w.winner.revisionId).catch(() => null))?.package ?? w.package : null);
    } catch (e) {
      setBlad(komunikatBledu(e));
    } finally {
      setTrwa(false);
    }
  };

  const zmienKto = (k: Kto) => { setKto(k); setWynik(null); setBlad(null); };
  const szkolyWidoczne = miasto ? szkolyMiasta : [];
  const wynikiWidoczne = fraza.trim().length >= 3 ? wyniki : [];

  return (
    <Dialog open onClose={onZamknij} maxWidth="md" fullWidth aria-labelledby="widok-skorki-tytul">
      <DialogTitle id="widok-skorki-tytul">Sprawdź widok użytkownika — skórka</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <ToggleButtonGroup exclusive size="small" value={kto} aria-label="Czyj widok" onChange={(_, v: Kto | null) => { if (v) zmienKto(v); }}>
            {(Object.keys(ETYKIETA_KTO) as Kto[]).map((k) => <ToggleButton key={k} value={k} aria-label={ETYKIETA_KTO[k]}>{ETYKIETA_KTO[k]}</ToggleButton>)}
          </ToggleButtonGroup>

          {kto === 'GOSC' ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>Niezalogowany — widzi wyłącznie skórki „Wszyscy".</Typography> : null}
          {kto === 'MIASTO' || kto === 'SZKOLA' ? (
            <Autocomplete options={miasta} value={miasto} getOptionLabel={(m) => m.name} isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => { setMiasto(v); setSzkola(null); setWynik(null); }}
              renderInput={(p) => <TextField {...p} label={kto === 'MIASTO' ? 'Miasto' : 'Miasto (zawęża listę szkół)'} />} />
          ) : null}
          {kto === 'SZKOLA' ? (
            <Autocomplete options={szkolyWidoczne} value={szkola} getOptionLabel={(s) => s.name} isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => { setSzkola(v); setWynik(null); }} renderInput={(p) => <TextField {...p} label="Szkoła" />} />
          ) : null}
          {kto === 'UZYTKOWNIK' ? (
            <Stack spacing={1}>
              <TextField label="Szukaj użytkownika (imię, nazwisko albo adres — min. 3 znaki)" value={fraza}
                onChange={(e) => setFraza(e.target.value)}
                slotProps={{ input: { endAdornment: szuka && fraza.trim().length >= 3 ? <CircularProgress size={16} /> : null } }} />
              {wynikiWidoczne.length ? (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} aria-label="Wyniki wyszukiwania">
                  {wynikiWidoczne.map((u) => (
                    <Chip key={u.userId} variant={uczen?.userId === u.userId ? 'filled' : 'outlined'} color={uczen?.userId === u.userId ? 'primary' : 'default'}
                      label={`${u.displayName} · ${u.maskedEmail ?? '—'} · ${u.schoolName ?? 'bez szkoły'} · ${u.cityName ?? '—'}`}
                      onClick={() => { setUczen(u); setWynik(null); }} />
                  ))}
                </Stack>
              ) : null}
            </Stack>
          ) : null}

          <Box><Button variant="contained" disabled={!odbiorca || trwa} onClick={() => { void sprawdz(); }}>{trwa ? 'Sprawdzam…' : 'Sprawdź'}</Button></Box>
          {blad ? <Alert severity="error" role="alert">{blad}</Alert> : null}

          {wynik ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} data-testid="widok-skorki-wynik">
              <Box sx={{ flex: 1 }}>
                {wynik.schoolName || wynik.cityName ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {wynik.schoolName ? `Szkoła: ${wynik.schoolName}` : ''}{wynik.schoolName && wynik.cityName ? ' · ' : ''}{wynik.cityName ? `Miasto: ${wynik.cityName}` : ''}
                  </Typography>
                ) : null}
                <Typography variant="overline" sx={{ display: 'block' }}>Skórka, która wygrała</Typography>
                <Typography variant="h6" component="p" data-testid="zwyciezca">{wynik.winner ? wynik.winner.name : 'Brak — zwykły wygląd aplikacji'}</Typography>
                <Typography variant="overline" sx={{ display: 'block', mt: 1 }}>Dlaczego</Typography>
                <Typography variant="body2" data-testid="dlaczego">
                  {wynik.winner
                    ? `${wynik.winner.legacy ? 'Publikacja globalna (V1)' : `Zastosowanie: ${ETYKIETA_ZAKRESU[wynik.winner.scope]} (${wynik.winner.scope})`} · priorytet ${wynik.winner.priority}. ${wynik.winner.reason}`
                    : wynik.reason}
                </Typography>
                {wynik.losers.length ? (
                  <>
                    <Typography variant="overline" sx={{ display: 'block', mt: 1 }}>Przegrane</Typography>
                    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }} data-testid="przegrane">
                      {wynik.losers.map((l) => (
                        <Typography component="li" variant="body2" key={`${l.assignmentId ?? 'v1'}-${l.revisionId}`}>
                          <Slad s={l} /> — {l.reason}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                ) : null}
              </Box>
              <Box sx={{ flex: 1 }}>
                {wynik.package ? <PodgladAplikacji paczka={paczkaZrodlowa ?? wynik.package} nazwa={wynik.winner?.name ?? 'skórka'} />
                  : <Typography variant="body2" sx={{ color: 'text.secondary' }}>Aplikacja pokaże swój zwykły wygląd.</Typography>}
              </Box>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onZamknij}>Zamknij</Button></DialogActions>
    </Dialog>
  );
}
